import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Plus, Trash2, Upload, Star, Package, PoundSterling, Clock, Repeat } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";

const TABS = ["orders", "reviews", "videos", "photos"];

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("orders");
  const [content, setContent] = useState({ reviews: [], videos: [], results: [] });

  const refresh = useCallback(() => {
    api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  const logout = async () => {
    localStorage.removeItem("aliver_admin_token");
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  if (user === undefined) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="animate-spin text-gold" size={32} />
      </main>
    );
  }

  if (!user) return <LoginForm onLogin={(u) => setUser(u)} />;

  if (user.role !== "admin") {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-5" data-testid="admin-not-authorized">
        <div className="max-w-sm w-full text-center rounded-[2rem] border border-champagne bg-white p-10">
          <h1 className="font-display text-3xl text-ink">Not authorized</h1>
          <p className="mt-3 text-sm text-[#8C8C8C]">This account does not have admin access.</p>
          <button onClick={logout} data-testid="admin-not-authorized-signout" className="mt-6 rounded-full bg-ink text-white text-sm font-semibold px-6 py-3 hover:bg-gold-deep transition-colors">
            Sign out
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-champagne bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <span className="font-display text-xl tracking-[0.15em] text-gold-deep">ALIVER · Content Studio</span>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-ink transition-colors"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </header>
      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="flex gap-2 mb-10">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`rounded-full px-5 py-2 text-sm font-semibold capitalize transition-colors duration-300 ${
                tab === t ? "bg-ink text-white" : "bg-white border border-champagne text-[#5C5C5C] hover:text-ink"
              }`}
            >
              {t === "orders" ? "Orders" : t === "photos" ? "Community Photos" : t === "videos" ? "UGC Videos" : "Reviews"}
            </button>
          ))}
        </div>
        {tab === "orders" && <OrdersTab />}
        {tab === "reviews" && <ReviewsTab reviews={content.reviews} refresh={refresh} />}
        {tab === "videos" && <VideosTab videos={content.videos} refresh={refresh} />}
        {tab === "photos" && <PhotosTab photos={content.results} refresh={refresh} />}
      </div>
    </main>
  );
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("aliver_admin_token", data.token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-cream flex items-center justify-center px-5" data-testid="admin-login-page">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-[2rem] border border-champagne bg-white p-9 shadow-[0_32px_80px_rgb(168,134,27,0.10)]"
      >
        <span className="font-display text-2xl tracking-[0.15em] text-gold-deep">ALIVER</span>
        <h1 className="mt-4 font-display text-3xl text-ink">Content Studio</h1>
        <p className="mt-2 text-sm text-[#8C8C8C]">Sign in to manage reviews, videos and photos.</p>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          data-testid="admin-email-input"
          className="mt-7 w-full rounded-xl border border-champagne bg-cream/60 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
        />
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          data-testid="admin-password-input"
          className="mt-3 w-full rounded-xl border border-champagne bg-cream/60 px-4 py-3 text-sm outline-none focus:border-gold transition-colors"
        />
        {error && (
          <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="mt-6 w-full inline-flex justify-center items-center gap-2 rounded-full bg-ink text-white font-semibold py-3.5 hover:bg-gold-deep transition-colors duration-300 disabled:opacity-70"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
        </button>
      </form>
    </main>
  );
}

function ReviewsTab({ reviews, refresh }) {
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/reviews", { name, rating: Number(rating), text });
      setName(""); setRating(5); setText("");
      toast.success("Review added");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review removed");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-8" data-testid="admin-reviews-tab">
      <div className="space-y-3">
        {reviews.length === 0 && <p className="text-sm text-[#8C8C8C]">No reviews yet — add your first genuine customer review.</p>}
        {reviews.map((r) => (
          <div key={r.id} className="rounded-2xl border border-champagne bg-white p-5 flex items-start justify-between gap-4" data-testid={`admin-review-${r.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-ink">{r.name}</span>
                <span className="inline-flex items-center gap-0.5 text-gold text-xs font-semibold">
                  <Star size={12} fill="currentColor" strokeWidth={0} /> {r.rating}
                </span>
                {r.placeholder && <span className="text-[10px] uppercase tracking-wider text-[#8C8C8C] bg-champagne/60 rounded-full px-2 py-0.5">placeholder</span>}
              </div>
              <p className="mt-1.5 text-sm text-[#5C5C5C] leading-relaxed">{r.text}</p>
            </div>
            <button onClick={() => remove(r.id)} data-testid={`admin-review-delete-${r.id}`} className="p-2 text-[#8C8C8C] hover:text-red-600 transition-colors" aria-label="Delete review">
              <Trash2 size={16} strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
      <form onSubmit={add} className="rounded-2xl border border-champagne bg-white p-6 h-fit space-y-3">
        <h3 className="font-display text-xl text-ink">Add a review</h3>
        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Customer name" data-testid="review-name-input"
          className="w-full rounded-xl border border-champagne bg-cream/60 px-4 py-2.5 text-sm outline-none focus:border-gold" />
        <select value={rating} onChange={(e) => setRating(e.target.value)} data-testid="review-rating-select"
          className="w-full rounded-xl border border-champagne bg-cream/60 px-4 py-2.5 text-sm outline-none focus:border-gold">
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
        </select>
        <textarea value={text} onChange={(e) => setText(e.target.value)} required rows={4} placeholder="Review text" data-testid="review-text-input"
          className="w-full rounded-xl border border-champagne bg-cream/60 px-4 py-2.5 text-sm outline-none focus:border-gold resize-none" />
        <button type="submit" disabled={saving} data-testid="review-add-button"
          className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-ink text-white text-sm font-semibold py-3 hover:bg-gold-deep transition-colors disabled:opacity-70">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={15} /> Add Review</>}
        </button>
      </form>
    </div>
  );
}

function VideosTab({ videos, refresh }) {
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    if (!caption.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("caption", caption);
      if (file) fd.append("file", file);
      await api.post("/admin/videos", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setCaption(""); setFile(null);
      e.target.reset();
      toast.success("Video slot saved");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/videos/${id}`);
      toast.success("Video removed");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-8" data-testid="admin-videos-tab">
      <div className="grid sm:grid-cols-2 gap-4">
        {videos.length === 0 && <p className="text-sm text-[#8C8C8C]">No video slots yet.</p>}
        {videos.map((v) => (
          <div key={v.id} className="rounded-2xl border border-champagne bg-white p-4" data-testid={`admin-video-${v.id}`}>
            <div className="aspect-[9/16] rounded-xl bg-champagne/40 overflow-hidden flex items-center justify-center">
              {v.storage_path ? (
                <video src={`${API}/files/${v.storage_path}`} controls playsInline preload="metadata" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#8C8C8C]">No video uploaded</span>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-ink truncate">{v.caption}</span>
              <button onClick={() => remove(v.id)} data-testid={`admin-video-delete-${v.id}`} className="p-1.5 text-[#8C8C8C] hover:text-red-600 transition-colors" aria-label="Delete video">
                <Trash2 size={15} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={upload} className="rounded-2xl border border-champagne bg-white p-6 h-fit space-y-3">
        <h3 className="font-display text-xl text-ink">Add a customer video</h3>
        <p className="text-xs text-[#8C8C8C] leading-relaxed">Vertical (9:16) short clips work best. Upload a real customer video file (mp4, mov, webm — max 200MB).</p>
        <input value={caption} onChange={(e) => setCaption(e.target.value)} required placeholder='Caption, e.g. "Morning skincare routine"' data-testid="video-caption-input"
          className="w-full rounded-xl border border-champagne bg-cream/60 px-4 py-2.5 text-sm outline-none focus:border-gold" />
        <input type="file" accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="video-file-input"
          className="w-full text-sm text-[#5C5C5C] file:mr-3 file:rounded-full file:border-0 file:bg-champagne file:px-4 file:py-2 file:text-xs file:font-semibold file:text-ink hover:file:bg-gold-light" />
        <button type="submit" disabled={saving} data-testid="video-upload-button"
          className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-ink text-white text-sm font-semibold py-3 hover:bg-gold-deep transition-colors disabled:opacity-70">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <><Upload size={15} /> Save Video</>}
        </button>
      </form>
    </div>
  );
}

function PhotosTab({ photos, refresh }) {
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a photo");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("caption", caption);
      fd.append("file", file);
      await api.post("/admin/results", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setCaption(""); setFile(null);
      e.target.reset();
      toast.success("Photo added");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/results/${id}`);
      toast.success("Photo removed");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid md:grid-cols-[1fr_360px] gap-8" data-testid="admin-photos-tab">
      <div className="grid sm:grid-cols-3 gap-4">
        {photos.length === 0 && <p className="text-sm text-[#8C8C8C] sm:col-span-3">No community photos yet — genuine customer photos will appear in the “Shared By You” section.</p>}
        {photos.map((p) => (
          <div key={p.id} className="rounded-2xl border border-champagne bg-white p-3" data-testid={`admin-photo-${p.id}`}>
            <img src={`${API}/files/${p.storage_path}`} alt={p.caption || "Customer photo"} className="aspect-[4/5] w-full rounded-xl object-cover" />
            <div className="mt-2.5 flex items-center justify-between gap-2">
              <span className="text-xs text-[#5C5C5C] truncate">{p.caption || "—"}</span>
              <button onClick={() => remove(p.id)} data-testid={`admin-photo-delete-${p.id}`} className="p-1.5 text-[#8C8C8C] hover:text-red-600 transition-colors" aria-label="Delete photo">
                <Trash2 size={15} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={upload} className="rounded-2xl border border-champagne bg-white p-6 h-fit space-y-3">
        <h3 className="font-display text-xl text-ink">Add a customer photo</h3>
        <p className="text-xs text-[#8C8C8C] leading-relaxed">Only upload genuine customer photos you have permission to use.</p>
        <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Optional caption" data-testid="photo-caption-input"
          className="w-full rounded-xl border border-champagne bg-cream/60 px-4 py-2.5 text-sm outline-none focus:border-gold" />
        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="photo-file-input"
          className="w-full text-sm text-[#5C5C5C] file:mr-3 file:rounded-full file:border-0 file:bg-champagne file:px-4 file:py-2 file:text-xs file:font-semibold file:text-ink hover:file:bg-gold-light" />
        <button type="submit" disabled={saving} data-testid="photo-upload-button"
          className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-ink text-white text-sm font-semibold py-3 hover:bg-gold-deep transition-colors disabled:opacity-70">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <><Upload size={15} /> Add Photo</>}
        </button>
      </form>
    </div>
  );
}

const STATUS_STYLES = {
  paid: "bg-green-100 text-green-800",
  pending: "bg-champagne/70 text-[#8C8C8C]",
  failed: "bg-red-100 text-red-700",
  expired: "bg-red-100 text-red-700",
};

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/admin/orders").then(({ data }) => setOrders(data.orders)).catch((err) => toast.error(formatApiError(err)));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (orders === null) {
    return <div className="flex justify-center py-16" data-testid="admin-orders-loading"><Loader2 className="animate-spin text-gold" size={28} /></div>;
  }

  const paid = orders.filter((o) => o.payment_status === "paid");
  const pending = orders.filter((o) => o.payment_status === "pending");
  const revenue = paid.reduce((s, o) => s + Number(o.amount || 0), 0);
  const subscribers = paid.filter((o) => o.lookup_key === "aliver_subscription").length;
  const visible = filter === "all" ? orders : orders.filter((o) => o.payment_status === filter);

  const stats = [
    { icon: PoundSterling, label: "Revenue", value: `£${revenue.toFixed(2)}`, testId: "stat-revenue" },
    { icon: Package, label: "Paid orders", value: paid.length, testId: "stat-paid" },
    { icon: Clock, label: "Pending", value: pending.length, testId: "stat-pending" },
    { icon: Repeat, label: "Subscribers", value: subscribers, testId: "stat-subscribers" },
  ];

  return (
    <div data-testid="admin-orders-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="rounded-2xl border border-champagne bg-white p-5" data-testid={s.testId}>
            <s.icon size={18} strokeWidth={1.5} className="text-gold-deep" />
            <p className="mt-3 font-display text-3xl text-ink leading-none">{s.value}</p>
            <p className="mt-1.5 text-[11px] uppercase tracking-[0.16em] text-[#8C8C8C]">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-2">
          {["all", "paid", "pending"].map((f) => (
            <button
              key={f}
              data-testid={`orders-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize transition-colors duration-300 ${
                filter === f ? "bg-ink text-white" : "bg-white border border-champagne text-[#5C5C5C] hover:text-ink"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="orders-refresh-button" className="text-sm font-semibold text-ink hover:text-gold-deep transition-colors">
          Refresh
        </button>
      </div>

      {visible.length === 0 && (
        <p className="text-sm text-[#8C8C8C] rounded-2xl border border-dashed border-gold-light bg-champagne/30 p-8 text-center" data-testid="orders-empty-state">
          No orders yet — they will appear here as soon as a customer completes checkout.
        </p>
      )}

      <div className="space-y-3">
        {visible.map((o, i) => {
          const isPaid = o.payment_status === "paid";
          const addr = o.shipping_address || {};
          const addrLine = [addr.line1, addr.line2, addr.city, addr.postal_code, addr.country].filter(Boolean).join(", ");
          const items = o.items || [];
          return (
            <div key={o.session_id} className="rounded-2xl border border-champagne bg-white p-5" data-testid={`order-row-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold tracking-wide uppercase ${STATUS_STYLES[o.payment_status] || STATUS_STYLES.pending}`} data-testid={`order-status-${i}`}>
                      {o.payment_status}
                    </span>
                    <span className="font-semibold text-sm text-ink" data-testid={`order-customer-name-${i}`}>
                      {o.customer_name || "Customer details pending"}
                    </span>
                  </div>
                  {o.customer_email && (
                    <p className="mt-1 text-xs text-[#8C8C8C]" data-testid={`order-customer-email-${i}`}>{o.customer_email}</p>
                  )}
                </div>
                <div className="text-right">
                  <span className="font-display text-2xl text-ink" data-testid={`order-amount-${i}`}>£{Number(o.amount || 0).toFixed(2)}</span>
                  <span className="ml-2 text-xs text-[#8C8C8C] uppercase">{o.currency}</span>
                </div>
              </div>

              <div className="mt-3 space-y-1" data-testid={`order-items-${i}`}>
                {items.length > 0 ? (
                  items.map((it, j) => (
                    <p key={j} className="text-sm text-[#3d3d3d]">
                      {it.name} <span className="text-[#8C8C8C]">× {it.quantity}</span>
                    </p>
                  ))
                ) : (
                  <p className="text-sm text-[#3d3d3d]">
                    {o.lookup_key === "aliver_subscription"
                      ? "24K Gold Hydrogel Eye Masks (60 patches) — monthly subscription"
                      : "24K Gold Hydrogel Eye Masks (60 patches)"}
                  </p>
                )}
              </div>

              <div className="mt-3 text-xs text-[#8C8C8C]">
                {o.created_at ? new Date(o.created_at).toLocaleString("en-GB") : ""} · {o.session_id}
              </div>

              {isPaid && (o.customer_phone || addrLine) && (
                <div className="mt-4 rounded-xl bg-cream/70 border border-champagne/60 p-4 text-sm text-[#3d3d3d] space-y-1" data-testid={`order-customer-${i}`}>
                  {o.customer_phone && <p><span className="font-semibold">Phone:</span> {o.customer_phone}</p>}
                  {addrLine && <p><span className="font-semibold">Deliver to:</span> {addrLine}</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
